import { Elements, useStripe } from '@stripe/react-stripe-js';
import { type Stripe } from '@stripe/stripe-js';
import { memo, type ReactElement, useEffect, useState } from 'react';

import { getStripePromise } from '~/components/AirStripe/getStripePromise';

type AirStripeChildren = ({ stripeIsLoaded }: { stripeIsLoaded: boolean }) => ReactElement | null;

type AirStripeProps = {
  children: AirStripeChildren;
};

export type AirStripeLoadedGateProps = {
  children: AirStripeChildren;
};

/** Reads the library's `useStripe()` from inside `<Elements>` to report once Stripe has loaded. */
const AirStripeLoadedGate = memo(({ children }: AirStripeLoadedGateProps) => {
  const stripe = useStripe();

  return children({ stripeIsLoaded: stripe !== null });
});

AirStripeLoadedGate.displayName = 'AirStripeLoadedGate';

/**
 * Use this component when you want to load Stripe to use any of it's elements.
 * It'll pass a boolean as a render prop to children so you can know for sure once
 * Stripe has finished loading
 * @param {ReactElement | null} children - The components you'd like to render that depend on Stripe being loaded
 */
const AirStripe = ({ children }: AirStripeProps) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  // Deferred to an effect (not computed at render) so SSR never injects the Stripe script.
  useEffect(() => {
    setStripePromise(getStripePromise());
  }, []);

  if (!stripePromise) {
    return children({ stripeIsLoaded: false });
  }

  return (
    <Elements stripe={stripePromise}>
      <AirStripeLoadedGate>{children}</AirStripeLoadedGate>
    </Elements>
  );
};

export default AirStripe;
